"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { reduceMotion } from "@/lib/motion";
import { ArrowRight, Check, Sparkle } from "@/components/ui/icons";
import { QSK_PULSE } from "@/content/quikskill";
import { cn } from "@/lib/utils";

type Phase = "idle" | "running" | "done";
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * A skills snapshot for one function. Capability bars fill as it enters; "Analyze gaps" then
 * reviews each skill in turn and ends on the one blocking the most roles. Auto-plays once in
 * view and can be replayed.
 */
export function SkillsMock() {
  const root = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [done, setDone] = useState(0); // skills reviewed so far
  const timers = useRef<number[]>([]);
  const played = useRef(false);

  const clear = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const run = useCallback(() => {
    clear();
    played.current = true;
    setPhase("running");
    setDone(0);
    QSK_PULSE.rows.forEach((_, i) => timers.current.push(window.setTimeout(() => setDone(i + 1), 620 + i * 600)));
    timers.current.push(window.setTimeout(() => setPhase("done"), 620 + QSK_PULSE.rows.length * 600 + 200));
  }, []);

  const reset = () => {
    clear();
    setPhase("idle");
    setDone(0);
  };

  useEffect(() => clear, []);

  useGSAP(
    () => {
      const el = root.current;
      if (!el || reduceMotion()) return;
      const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: "top 85%", toggleActions: PLAY_ONCE }, delay: 0.9 });
      tl.fromTo(
        gsap.utils.toArray("[data-mock-row]", el),
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.7, ease: "silk", stagger: 0.07 },
      );
      tl.fromTo(gsap.utils.toArray("[data-bar]", el), { scaleX: 0 }, { scaleX: 1, duration: 1.3, ease: "silk", stagger: 0.08 }, 0.3);
      tl.call(() => {
        timers.current.push(window.setTimeout(() => !played.current && run(), 1600));
      });
    },
    { scope: root },
  );

  const running = phase === "running";

  return (
    <div
      ref={root}
      className="w-full rounded-[22px] border border-white/[0.09] bg-[#0b0b0c] p-4 text-left text-white shadow-[0_40px_100px_-40px_rgba(0,0,0,0.9)] md:p-5"
    >
      <div data-mock-row="" className="flex items-center justify-between gap-3">
        <p className="truncate text-[11.5px] text-white/45">{QSK_PULSE.path}</p>
        <span
          className={cn(
            "shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-medium transition-colors duration-500",
            phase === "done" ? "bg-mint text-ink" : "bg-mint/10 text-mint",
          )}
        >
          {phase === "done" ? "Gaps found" : "Live"}
        </span>
      </div>

      {/* Two headline numbers */}
      <div data-mock-row="" className="mt-4 grid grid-cols-2 gap-2">
        {[QSK_PULSE.coverage, QSK_PULSE.certs].map((m) => (
          <div key={m.label} className="rounded-[14px] bg-white/[0.04] p-3.5">
            <p className="text-[11px] text-white/45">{m.label}</p>
            <p className="mt-1 text-[15px] font-medium">{m.value}</p>
            <p className="mt-0.5 text-[11px] text-white/40">{m.meta}</p>
          </div>
        ))}
      </div>

      {/* Skill capability bars */}
      <ul className="mt-2 space-y-1.5">
        {QSK_PULSE.rows.map((r, i) => {
          const reviewed = done > i;
          return (
            <li data-mock-row="" key={r.name} className="rounded-[14px] bg-white/[0.04] p-3.5">
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 flex-1 truncate text-[13px]">{r.name}</span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className={cn("text-[10.5px] font-medium", r.state === "down" ? "text-amber-400" : "text-white/45")}>{r.metricLabel}</span>
                  {reviewed && <Check className="size-3.5 text-mint" strokeWidth={2.4} />}
                </span>
              </div>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/[0.08]">
                <div
                  data-bar=""
                  style={{ width: `${r.metric}%` }}
                  className={cn("h-full origin-left rounded-full", r.state === "down" ? "bg-amber-400/70" : "bg-mint")}
                />
              </div>
            </li>
          );
        })}
      </ul>

      {/* Closing the gaps */}
      <div data-mock-row="" className="mt-2 flex items-center justify-between gap-3 rounded-[14px] bg-white/[0.04] p-3.5">
        <span className="text-[12px] text-white/55">
          Gaps closed <span className="font-medium text-white">{QSK_PULSE.closed.done}</span>
          <span className="text-white/35"> / {QSK_PULSE.closed.total}</span>
        </span>
        <span className="flex items-center gap-1.5 text-[11px] text-white/40">
          {QSK_PULSE.closed.note}
          <ArrowRight className="size-3 shrink-0" />
        </span>
      </div>

      <div className="mt-3 border-t border-white/[0.07] pt-3">
        <AnimatePresence mode="wait" initial={false}>
          {phase === "done" ? (
            <motion.div
              key="summary"
              initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.08em] text-mint">
                <Sparkle className="size-3.5 shrink-0" />
                Skill gap analysis
              </p>
              <p className="mt-2 text-[12.5px] leading-[1.5] text-white/80">{QSK_PULSE.footer}</p>
              <div className="mt-3 flex justify-end">
                <button type="button" onClick={reset} className="text-[11.5px] font-medium text-white/60 underline-offset-4 hover:text-white hover:underline">
                  Replay
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.button
              key="cta"
              type="button"
              onClick={run}
              disabled={running}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: EASE }}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-white/[0.06] py-2.5 text-[12.5px] font-medium text-white/85 transition-colors hover:bg-white/[0.1] disabled:opacity-60"
            >
              <Sparkle className={cn("size-3.5 shrink-0 text-mint", running && "animate-pulse")} />
              {running ? "Analyzing gaps…" : "Analyze skill gaps"}
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
